import { useEffect, useRef, useCallback } from 'react';
import { confettiConfig } from '../constants';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  shape: 'square' | 'circle';
  opacity: number;
}

interface ConfettiCanvasProps {
  active: boolean;
  onComplete?: () => void;
}

export function ConfettiCanvas({ active, onComplete }: ConfettiCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number | null>(null);
  const hasTriggered = useRef(false);

  const createParticles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const particles: Particle[] = [];

    for (let i = 0; i < confettiConfig.particleCount; i++) {
      const angle = (Math.random() * Math.PI * 2);
      const velocity = confettiConfig.startVelocity * (0.5 + Math.random() * 0.5);
      const spread = (Math.random() - 0.5) * confettiConfig.spread * (Math.PI / 180);

      particles.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle + spread) * velocity,
        vy: Math.sin(angle + spread) * velocity - 10, // Upward bias
        color: confettiConfig.colors[Math.floor(Math.random() * confettiConfig.colors.length)],
        size: 6 + Math.random() * 6,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 15,
        shape: confettiConfig.shapes[Math.floor(Math.random() * confettiConfig.shapes.length)],
        opacity: 1,
      });
    }

    particlesRef.current = particles;
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    let activeParticles = 0;

    particlesRef.current.forEach((particle) => {
      // Physics
      particle.vy += confettiConfig.gravity;
      particle.vx *= confettiConfig.decay;
      particle.vy *= confettiConfig.decay;
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.rotation += particle.rotationSpeed;

      // Fade out when below canvas
      if (particle.y > canvas.height - 50) {
        particle.opacity -= 0.02;
      }

      if (particle.opacity <= 0) return;
      activeParticles++;

      // Draw
      ctx.save();
      ctx.translate(particle.x, particle.y);
      ctx.rotate((particle.rotation * Math.PI) / 180);
      ctx.globalAlpha = particle.opacity;
      ctx.fillStyle = particle.color;

      if (particle.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, particle.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(
          -particle.size / 2,
          -particle.size / 2,
          particle.size,
          particle.size
        );
      }

      ctx.restore();
    });

    if (activeParticles > 0) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      onComplete?.();
    }
  }, [onComplete]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size to viewport
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (active && !hasTriggered.current) {
      hasTriggered.current = true;
      createParticles();
      animate();
    }
  }, [active, createParticles, animate]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[100]"
      style={{ mixBlendMode: 'normal' }}
    />
  );
}
